import type { ActionPerformed } from '@capacitor/push-notifications';
import { PushNotifications } from '@capacitor/push-notifications';
import { type NavigateFn, useNavigate } from '@tanstack/react-router';
import type {
  ChatMessageReceivedNotificationData,
  DuoFeedbackReceivedNotificationData,
  DuoFormedNotificationData,
  DuoLevelUpNotificationData,
  LikeReceivedNotificationData,
  UserNotification,
  VoiceCallUserJoinedNotificationData,
} from 'generated/openapi';
import { useEffect } from 'react';

import { isNativePlatform } from '@/shared/utils/platform';

// ============================================================================
// Payload Parsing
// ============================================================================

interface FcmDataPayload {
  notification?: string; // JSON stringifies UserNotification
}

function parseNotificationPayload(
  data: FcmDataPayload | undefined,
): UserNotification | undefined {
  if (!data?.notification) {
    return undefined;
  }

  try {
    return JSON.parse(data.notification) as UserNotification;
  } catch {
    return undefined;
  }
}

// ============================================================================
// Notification Handlers
// ============================================================================

function handleDuoFormed(
  data: DuoFormedNotificationData,
  navigate: NavigateFn,
) {
  void navigate({
    to: '/my-duos/$id',
    params: { id: data.otherUserId },
  });
}

function handleDuoFeedbackReceived(
  data: DuoFeedbackReceivedNotificationData,
  navigate: NavigateFn,
) {
  void navigate({
    to: '/my-duos/$id',
    params: { id: data.fromUserId },
  });
}

function handleDuoLevelUp(
  data: DuoLevelUpNotificationData,
  navigate: NavigateFn,
) {
  void navigate({
    to: '/my-duos/$id',
    params: { id: data.otherUserId },
  });
}

function handleLikeReceived(
  _data: LikeReceivedNotificationData,
  navigate: NavigateFn,
) {
  void navigate({
    to: '/likes',
  });
}

function handleChatMessageReceived(
  data: ChatMessageReceivedNotificationData,
  navigate: NavigateFn,
) {
  void navigate({
    to: '/duo-chats/$duoId',
    params: { duoId: data.author.userId },
  });
}

function handleVoiceCallUserJoined(
  data: VoiceCallUserJoinedNotificationData,
  navigate: NavigateFn,
) {
  void navigate({
    to: '/duo-chats/$duoId',
    params: { duoId: data.userId },
  });
}

function handleNotificationAction(
  notification: UserNotification,
  navigate: NavigateFn,
) {
  switch (notification.data.type) {
    case 'DUO_FORMED':
      handleDuoFormed(notification.data, navigate);
      break;
    case 'DUO_FEEDBACK_RECEIVED':
      handleDuoFeedbackReceived(notification.data, navigate);
      break;
    case 'DUO_LEVEL_UP':
      handleDuoLevelUp(notification.data, navigate);
      break;
    case 'LIKE_RECEIVED':
      handleLikeReceived(notification.data, navigate);
      break;
    case 'CHAT_MESSAGE_RECEIVED':
      handleChatMessageReceived(notification.data, navigate);
      break;
    case 'VOICE_CALL_USER_JOINED':
      handleVoiceCallUserJoined(notification.data, navigate);
      break;
  }
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to listen for native push notification actions (iOS/Android only)
 * Uses @capacitor/push-notifications which doesn't support web
 */
export function useNativePushListeners() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isNativePlatform()) {
      return;
    }

    const actionPerformedListener = PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (action: ActionPerformed) => {
        const payload = action.notification.data as FcmDataPayload | undefined;
        const notification = parseNotificationPayload(payload);

        if (notification) {
          handleNotificationAction(notification, navigate);
        }
      },
    );

    return () => {
      void actionPerformedListener.then(listener => listener.remove());
    };
  }, [navigate]);
}
